import { useEffect, useRef, useState } from 'react';

import type { StrategyListItem } from '../../../../../bridge/types/strategy.ts';

// How long a removed orb stays mounted to collapse out. Matches the repo's
// useExitAnimation constant so every exit in the app reads at the same pace.
const EXIT_MS = 250;
// How long an arriving id keeps its "entering" flag: the width clip plus the settle.
const ENTER_MS = 200 + 360;

export interface DockMembershipMotion {
	// Ids that just JOINED the dock — they crossfade up from the ghost palette.
	enteringIds: ReadonlySet<string>;
	// Rows that just LEFT, kept mounted for one collapse beat.
	leavingItems: StrategyListItem[];
}

// Tracks which dock rows are arriving and which are leaving, so the dock can animate its
// collection changing.
//
// This lives at the StrategyDock level on purpose: DockBar is REMOUNTED every time the
// menu opens or closes (the bar moves into the menu surface), so anything derived from
// DockBar's own mount would replay the whole entrance on every open. Only a hook that
// outlives those remounts can tell "just added" from "has been there for an hour".
//
// `settled` gates the first population: until the strategy list is authoritative it is empty,
// so without the gate the first real response would look like every strategy had just arrived
// at once.
export function useDockMembershipMotion(
	items: StrategyListItem[],
	settled: boolean,
): DockMembershipMotion {
	const seenRef = useRef<Set<string> | null>(null);
	// Every row we've seen, so a departure can still be rendered after it has dropped
	// out of `items`.
	const lastKnownRef = useRef(new Map<string, StrategyListItem>());
	// One timer per id, so an add→remove→re-add inside the exit window can't let a stale
	// timer drop a row that is legitimately present again.
	const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
	const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set());
	const [leavingItems, setLeavingItems] = useState<StrategyListItem[]>([]);

	for (const item of items) lastKnownRef.current.set(item.id, item);

	useEffect(() => {
		// Nothing authoritative yet: don't seed and don't animate. The next run with
		// settled=true adopts whatever is present as pre-existing.
		if (!settled) return;

		const currentKeys = new Set(items.map((i) => i.id));

		// First authoritative pass: adopt the current membership silently.
		if (seenRef.current === null) {
			seenRef.current = currentKeys;
			return;
		}

		const seen = seenRef.current;
		const entered = [...currentKeys].filter((k) => !seen.has(k));
		const exited = [...seen].filter((k) => !currentKeys.has(k));
		if (entered.length === 0 && exited.length === 0) return;

		seenRef.current = currentKeys;
		const timers = timersRef.current;

		const schedule = (key: string, ms: number, fn: () => void) => {
			const pending = timers.get(key);
			if (pending) clearTimeout(pending);
			timers.set(
				key,
				setTimeout(() => {
					timers.delete(key);
					fn();
				}, ms),
			);
		};

		if (entered.length > 0) {
			setEnteringIds((prev) => new Set([...prev, ...entered]));
			for (const key of entered) {
				schedule(`enter-${key}`, ENTER_MS, () =>
					setEnteringIds((prev) => {
						if (!prev.has(key)) return prev;
						const next = new Set(prev);
						next.delete(key);
						return next;
					}),
				);
			}
		}

		if (exited.length > 0) {
			const exitedItems = exited
				.map((key) => lastKnownRef.current.get(key))
				.filter((v): v is StrategyListItem => Boolean(v));
			if (exitedItems.length > 0) {
				const exitedKeys = new Set(exited);
				setLeavingItems((prev) => [
					...prev.filter((p) => !exitedKeys.has(p.id)),
					...exitedItems,
				]);
			}
			for (const key of exited) {
				schedule(`exit-${key}`, EXIT_MS, () =>
					setLeavingItems((prev) => prev.filter((p) => p.id !== key)),
				);
			}
		}
	}, [items, settled]);

	// Clear pending timers on unmount so no state update lands after teardown.
	useEffect(
		() => () => {
			for (const timer of timersRef.current.values()) clearTimeout(timer);
			timersRef.current.clear();
		},
		[],
	);

	// A row that returns while its exit timer is still pending must not render twice
	// (once live, once collapsing).
	const liveKeys = new Set(items.map((i) => i.id));
	return {
		enteringIds,
		leavingItems: leavingItems.filter((item) => !liveKeys.has(item.id)),
	};
}
